import {
  ArrDepTime,
  IFindTicketForm,
  PriceRange,
  TransferTime,
} from 'entities/ticket/models/find-ticket/models'

import { TypeRefundTicketAvia } from 'shared/constants'
import { GenerateToken } from 'shared/lib/generation-token'
import { formatTimeToString } from 'shared/lib/ticketRender'
import {
  CountriesSearchBookitResult,
  CountriesSearchResult,
  Pagination,
  Response,
} from 'shared/models/general.ts'
import { AirportFlyDirection, Carrier, TicketItem } from 'shared/models/ticket'

import { ICalendarPriceResponse } from './Calendar'
import { clientApi } from './clientApi'

interface AviaSearchParams {
  isDifficultSearch: boolean
}

interface PaginationPage extends IFindTicketForm, Pagination {
  rangePrice?: PriceRange
  searchParams?: AviaSearchParams
}

interface ResponsePage extends Pagination, Response<TicketItem[]> {
  price_range: PriceRange
  airlines: Carrier[]
  airports: AirportFlyDirection[]
  nearby_price?: ICalendarPriceResponse[]
  transfer_duration?: TransferTime
}

interface FilterAirports {
  [key: number]: {
    departure: string[] | undefined
    transfer: string[] | undefined
    arrival: string[] | undefined
  }
}

interface TimeRange {
  from: string
  to: string
}

interface RouteTimeItem {
  dep: TimeRange[]
  arr: TimeRange[]
}

const buildRouteTimes = (
  timeFilters?: ArrDepTime[],
): RouteTimeItem[] | undefined => {
  if (!timeFilters || timeFilters.length === 0) {
    return undefined
  }

  const routeTimesArray: RouteTimeItem[] = []
  let hasAnyFilters = false

  timeFilters.forEach((item) => {
    const routeTime: RouteTimeItem = { dep: [], arr: [] }

    if (item.departure) {
      const departures = Array.isArray(item.departure)
        ? item.departure
        : [item.departure]

      departures.forEach((dep) => {
        if (dep.from || dep.to) {
          routeTime.dep.push({
            from: formatTimeToString(dep.from, '00', '00'),
            to: formatTimeToString(dep.to, '24', '00'),
          })
          hasAnyFilters = true
        }
      })
    }

    if (item.arrival) {
      const arrivals = Array.isArray(item.arrival)
        ? item.arrival
        : [item.arrival]

      arrivals.forEach((arr) => {
        if (arr.from || arr.to) {
          routeTime.arr.push({
            from: formatTimeToString(arr.from, '00', '00'),
            to: formatTimeToString(arr.to, '24', '00'),
          })
          hasAnyFilters = true
        }
      })
    }

    routeTimesArray.push(routeTime)
  })

  return hasAnyFilters ? routeTimesArray : undefined
}

export const SearchApi = {
  searchCity(name: string) {
    return clientApi.get<CountriesSearchResult>(
      `/flights/cities?name=${name || ''}`,
    )
  },
  searchCityBookit(name: string) {
    return clientApi.get<CountriesSearchBookitResult>(
      `/flights/bookit/cities?name=${name || ''}`,
    )
  },
  searchTicket(
    params: PaginationPage,
    token: GenerateToken,
    abort?: AbortSignal,
  ) {
    const filter = params.filter
    const isDifficultSearch = params?.searchParams?.isDifficultSearch

    const dataQueries = {
      cabin_class: params.cabin_class,
      adults: params?.adult,
      children: params?.child,
      infants: params?.infant,
      infants_with_place: params?.infants_with_place,
      page: params.page,
      per_page: params.per_page,
    }

    const resultAirports = filter?.airports?.reduce((acc, item, index) => {
      acc[index] = {
        departure: item.departure,
        transfer: item.transfer,
        arrival: item.arrival,
      }

      return acc
    }, {} as FilterAirports)

    const routeTimes = buildRouteTimes(filter?.time)

    const simpleSearch = params?.directions[0]

    const direction = isDifficultSearch
      ? params?.directions?.map((item) => ({
          from_: item.from?.cityIataCode,
          to: item.to?.cityIataCode,
          date_: item.date,
        }))
      : [
          {
            date_: simpleSearch?.date,
            to: simpleSearch?.to?.cityIataCode,
            from_: simpleSearch?.from?.cityIataCode,
          },
        ]

    if (!isDifficultSearch && simpleSearch?.back_date) {
      direction.push({
        from_: simpleSearch?.to?.cityIataCode,
        to: simpleSearch?.from?.cityIataCode,
        date_: simpleSearch?.back_date,
      })
    }

    const filterTickets = {
      price_min: filter?.rangePrice?.min,
      price_max: filter?.rangePrice?.max,
      baggage: filter?.baggage ? filter?.baggage : undefined,
      stops: filter?.transfers ? filter?.transfers : undefined,
      airline_codes: filter?.airlines && filter?.airlines?.map((it) => it.code),
      airports_ids: resultAirports,
      route_times: routeTimes,
      directions: direction,
      is_charter: !filter?.isOffCharter,
      without_night_transfer: filter?.isWithNightTransition,
      without_recheck: filter?.isWithRepeatRegistration,
      without_airport_change: filter?.isWithChangeAirport,
      transfer_duration_max: filter?.durationOfTransfers?.max_duration,
      transfer_duration_min: filter?.durationOfTransfers?.min_duration,
      is_refund: TypeRefundTicketAvia[filter?.refundTicket || 'All']?.value,
    }

    return clientApi.post<ResponsePage>(
      `/flights/recommendations-complex-route`,
      filterTickets,
      {
        params: dataQueries,
        signal: abort,
        headers: {
          'X-Token-A': token.generatedToken,
          'X-Token-B': token.modifiedToken,
        },
      },
    )
  },
}
